import { useDispatch } from 'react-redux';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  useGetUserPreferencesQuery,
  useUpdateUserSettingsMutation,
} from '../components/AdminWelcomeModal/apiSlice';
import { UserSettings } from '../models/UserSettings';
import { runTutorial } from '../store/shared/platformTourSlice';

interface useVisitedPagesHooks {
  performFirstTimeVisit: (monitoredElementSelector: string) => void;
  isFirstVisitThen: (callback?: () => void) => void;
  isLoading: boolean;
}

export default function useAdminVisitedPages(
  page: string,
): useVisitedPagesHooks {
  const dispatch = useDispatch();
  const {
    data: userPreferences,
    isLoading,
    isFetching,
  } = useGetUserPreferencesQuery();
  const [fetchingVisits, setFetchingVisits] = useState(true);
  const hasHandledFirstVisitRef = useRef(false);

  const [updateUserSettings] = useUpdateUserSettingsMutation();

  const isFirstVisit = useCallback(
    (callback: () => void) => {
      if (!isLoading && !isFetching && !hasHandledFirstVisitRef.current) {
        const visitedPages = userPreferences?.userSettings?.visitedPages || [];
        if (!visitedPages.includes(page)) {
          hasHandledFirstVisitRef.current = true;
          updateUserSettings({
            ...userPreferences?.userSettings,
            visitedPages: [...visitedPages, page],
          } as UserSettings).unwrap();
          callback();
        }
      }
    },
    [isLoading, isFetching, userPreferences, page, updateUserSettings],
  );

  const performFirstTimeVisit = useCallback(
    (monitoredElementSelector: string) => {
      isFirstVisit(() => {
        let limitInterval = 0;
        const interval = setInterval(() => {
          if (!fetchingVisits) {
            if (document.querySelector(monitoredElementSelector)) {
              clearInterval(interval);
              dispatch(runTutorial());
            }
            limitInterval += 1;
            // if 1 minute has passed clear interval automatically to avoid memory leak
            if (limitInterval === 600) {
              clearInterval(interval);
            }
          }
        }, 100);
      });
    },
    [isFirstVisit, fetchingVisits, dispatch],
  );

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setFetchingVisits(false);
    } else {
      setFetchingVisits(true);
    }
  }, [isLoading, isFetching]);

  const safeIsFirstVisitThen = useCallback(
    (callback?: () => void) => {
      if (isLoading || isFetching) {
        return;
      }
      if (callback) {
        isFirstVisit(callback);
      }
    },
    [isLoading, isFetching, isFirstVisit],
  );

  return {
    performFirstTimeVisit,
    isFirstVisitThen: safeIsFirstVisitThen,
    isLoading: fetchingVisits,
  };
}
